import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getClientAiProfile,
  refreshClientAiProfile,
  confirmClientAiProfile,
  confirmClientAiContactRole,
} from '../utils/api';

const intentLabels = {
  buy: { ar: 'شراء', en: 'Buy' },
  rent: { ar: 'إيجار', en: 'Rent' },
  inquiry: { ar: 'استفسار', en: 'Inquiry' },
  unknown: { ar: 'غير محدد', en: 'Unknown' },
};

const propertyLabels = {
  apartment: { ar: 'شقة', en: 'Apartment' },
  villa: { ar: 'فيلا', en: 'Villa' },
  land: { ar: 'أرض', en: 'Land' },
  office: { ar: 'مكتب', en: 'Office' },
  commercial: { ar: 'تجاري', en: 'Commercial' },
  unknown: { ar: 'غير محدد', en: 'Unknown' },
};

const levelLabels = {
  high: { ar: 'عالية', en: 'High' },
  medium: { ar: 'متوسطة', en: 'Medium' },
  low: { ar: 'منخفضة', en: 'Low' },
  unknown: { ar: 'غير محدد', en: 'Unknown' },
};

const roleLabels = {
  end_client: { ar: 'عميل مباشر', en: 'End client' },
  agent_broker: { ar: 'وسيط / ممثل', en: 'Agent / broker' },
  unknown: { ar: 'غير محدد', en: 'Unknown' },
};

function formatBudget(budget, language) {
  if (!budget || (budget.min == null && budget.max == null)) return null;
  if (budget.min === 'unknown' || budget.max === 'unknown') return null;
  const cur = language === 'ar' ? 'ريال' : 'SAR';
  const min = budget.min != null && budget.min !== '' ? Number(budget.min) : null;
  const max = budget.max != null && budget.max !== '' ? Number(budget.max) : null;
  if (Number.isNaN(min) && Number.isNaN(max)) return null;
  if (min != null && max != null && !Number.isNaN(min) && !Number.isNaN(max)) {
    return `${min.toLocaleString()} – ${max.toLocaleString()} ${cur}`;
  }
  if (max != null && !Number.isNaN(max)) return `${max.toLocaleString()} ${cur}`;
  if (min != null && !Number.isNaN(min)) return `${min.toLocaleString()} ${cur}`;
  return null;
}

function shortenSummary(text, maxLen = 180) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  // Keep the full short summary (broker note + request) when it fits
  if (t.length <= maxLen) return t;
  const within = t.slice(0, maxLen);
  const lastStop = Math.max(
    within.lastIndexOf('.'),
    within.lastIndexOf('۔'),
    within.lastIndexOf('؟'),
    within.lastIndexOf('!'),
    within.lastIndexOf('?')
  );
  if (lastStop > Math.floor(maxLen * 0.45)) {
    return within.slice(0, lastStop + 1).trim();
  }
  return `${within.trim()}…`;
}

const ClientAiProfile = ({
  clientId,
  language = 'ar',
  isOwner = false,
  onApplied,
  listSummary = null,
  listDraft = null,
  compact = false,
}) => {
  const isRTL = language === 'ar';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await getClientAiProfile(clientId);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      await load();
      if (attempts >= 24) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRefreshing(false);
      }
    }, 5000);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await refreshClientAiProfile(clientId);
      startPolling();
      setTimeout(() => setRefreshing(false), 120000);
    } catch (e) {
      setRefreshing(false);
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);
    try {
      await confirmClientAiProfile(clientId);
      await load();
      if (onApplied) onApplied();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmRole = async (role) => {
    setRoleSaving(true);
    setError(null);
    try {
      await confirmClientAiContactRole(clientId, role);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setRoleSaving(false);
    }
  };

  if (!clientId) return null;

  const apiDraft = data?.draft && typeof data.draft === 'object' ? data.draft : {};
  const draft = {
    ...(listDraft && typeof listDraft === 'object' ? listDraft : {}),
    ...apiDraft,
  };
  const summaryRaw =
    (data?.summary && String(data.summary).trim()) ||
    (listSummary && String(listSummary).trim()) ||
    '';
  const summaryText = shortenSummary(summaryRaw, compact ? 160 : 200);
  const meaningfulIntent = draft.intent && draft.intent !== 'unknown';
  const apiHasRow = data?.status && data.status !== 'empty';
  const hasProfile = !!(
    summaryText ||
    meaningfulIntent ||
    (apiHasRow && draft.readiness && draft.readiness !== 'unknown')
  );
  const contactRole = draft.contact_role || 'unknown';
  const roleConfirmed = draft.contact_role_confirmed === true;
  const offeredLabel = formatBudget(draft.offered_price, language);
  const chips = [
    contactRole !== 'unknown'
      ? {
          label: language === 'ar' ? 'دور الرقم' : 'Contact role',
          value: roleLabels[contactRole]?.[language] || contactRole,
        }
      : null,
    meaningfulIntent
      ? {
          label: language === 'ar' ? 'الغرض' : 'Intent',
          value: intentLabels[draft.intent]?.[language] || draft.intent,
        }
      : null,
    draft.property_type && draft.property_type !== 'unknown'
      ? {
          label: language === 'ar' ? 'النوع' : 'Type',
          value: propertyLabels[draft.property_type]?.[language] || draft.property_type,
        }
      : null,
    formatBudget(draft.budget, language)
      ? {
          label: language === 'ar' ? 'ميزانية العميل' : 'Client budget',
          value: formatBudget(draft.budget, language),
        }
      : null,
    offeredLabel
      ? {
          label: language === 'ar' ? 'سعر العرض' : 'Offered price',
          value:
            draft.offered_price?.status === 'refused'
              ? `${offeredLabel} (${language === 'ar' ? 'مرفوض' : 'refused'})`
              : offeredLabel,
        }
      : null,
    draft.readiness && draft.readiness !== 'unknown'
      ? {
          label: language === 'ar' ? 'الجاهزية' : 'Readiness',
          value: levelLabels[draft.readiness]?.[language] || draft.readiness,
          tone: draft.readiness,
        }
      : null,
    draft.priority && draft.priority !== 'unknown'
      ? {
          label: language === 'ar' ? 'الأولوية' : 'Priority',
          value: levelLabels[draft.priority]?.[language] || draft.priority,
          tone: draft.priority,
        }
      : null,
  ].filter(Boolean);

  const showRoleConfirm =
    hasProfile &&
    !roleConfirmed &&
    (contactRole === 'agent_broker' || contactRole === 'unknown' || contactRole === 'end_client');

  return (
    <div
      style={{
        margin: compact ? '0 0 14px' : '0 0 20px',
        borderRadius: compact ? 10 : 14,
        border: compact ? '1px solid #f3e8d8' : '1px solid #fed7aa',
        background: compact ? '#fffdf9' : '#fffbeb',
        direction: isRTL ? 'rtl' : 'ltr',
        overflow: 'hidden',
      }}
    >
      {!compact && <div style={{ height: 3, background: '#ea580c' }} />}
      <div style={{ padding: compact ? 12 : 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontWeight: 800, color: '#c2410c', fontSize: '0.95rem' }}>
            {language === 'ar' ? 'ملخص الوكيل الذكي' : 'Agent AI summary'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid #fdba74',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {refreshing
                ? language === 'ar'
                  ? 'جاري التحديث…'
                  : 'Updating…'
                : language === 'ar'
                  ? 'تحديث'
                  : 'Refresh'}
            </button>
            {isOwner && hasProfile && data?.status === 'draft' && (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  borderRadius: 8,
                  border: 'none',
                  background: '#ea580c',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {confirming
                  ? language === 'ar'
                    ? 'جاري الحفظ…'
                    : 'Saving…'
                  : language === 'ar'
                    ? 'تطبيق على العميل'
                    : 'Apply to client'}
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div style={{ color: '#64748b', fontSize: 13 }}>
            {language === 'ar' ? 'جاري التحميل…' : 'Loading…'}
          </div>
        )}

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>}

        {!loading && !hasProfile && (
          <div style={{ color: '#64748b', fontSize: 13, lineHeight: 1.55 }}>
            {language === 'ar'
              ? 'لا يوجد ملخص بعد. اضغط «تحديث» إن وُجدت محادثة واتساب.'
              : 'No summary yet. Tap Refresh if a WhatsApp thread exists.'}
          </div>
        )}

        {hasProfile && (
          <>
            {summaryText && (
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: '#1c1917',
                  fontWeight: 500,
                }}
              >
                {summaryText}
              </p>
            )}

            {showRoleConfirm && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #fdba74',
                  background: '#fff7ed',
                  fontSize: 13,
                  color: '#9a3412',
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {language === 'ar' ? 'تصنيف جهة الاتصال' : 'Contact classification'}
                </div>
                <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
                  {draft.contact_role_reason ||
                    (language === 'ar'
                      ? `اقتراح النظام: ${roleLabels[contactRole]?.ar || contactRole}`
                      : `System suggestion: ${roleLabels[contactRole]?.en || contactRole}`)}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={roleSaving}
                    onClick={() => handleConfirmRole('end_client')}
                    style={{
                      padding: '6px 10px',
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid #fdba74',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'ar' ? 'تأكيد: عميل مباشر' : 'Confirm: end client'}
                  </button>
                  <button
                    type="button"
                    disabled={roleSaving}
                    onClick={() => handleConfirmRole('agent_broker')}
                    style={{
                      padding: '6px 10px',
                      fontSize: 12,
                      borderRadius: 8,
                      border: 'none',
                      background: '#ea580c',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'ar' ? 'تأكيد: وسيط/ممثل' : 'Confirm: agent/broker'}
                  </button>
                </div>
              </div>
            )}

            {chips.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {chips.map((chip) => (
                  <Chip key={chip.label} label={chip.label} value={chip.value} tone={chip.tone} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function Chip({ label, value, tone }) {
  const styles =
    tone === 'high'
      ? { bg: '#ea580c', border: '#c2410c', text: '#ffffff', label: '#ffedd5' }
      : tone === 'medium'
        ? { bg: '#fef3c7', border: '#d97706', text: '#92400e', label: '#b45309' }
        : tone === 'low'
          ? { bg: '#f1f5f9', border: '#94a3b8', text: '#334155', label: '#64748b' }
          : { bg: '#ffffff', border: '#e2e8f0', text: '#0f172a', label: '#64748b' };

  return (
    <div
      style={{
        background: styles.bg,
        border: `1.5px solid ${styles.border}`,
        borderRadius: 10,
        padding: '8px 12px',
        minWidth: 96,
      }}
    >
      <div style={{ fontSize: 11, color: styles.label, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: styles.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default ClientAiProfile;
